// Nine Lives '84 - the three.js world renderer.
//
// Owns the WebGL renderer, scene, lights, camera, the alley scenery and
// its inhabitants, the current room, the cat view, particles and
// post-processing. The simulation is the single source of truth: call
// `commit()` after every tick and `render(alpha, dt)` per frame. `bind()`
// switches to a new Game; `onEvent()` turns simulation events into
// particles and camera shake.
//
// Scene changes: the simulation holds still (`game.freeze`) and the view
// uses that time to dolly the camera toward where Tabs went, fade to
// black, swap the scenery at the midpoint, and dolly back out from where
// he arrives.

import * as THREE from 'three';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { WATER_SURFACE, WORLD_H, WORLD_W } from '../sim/constants';
import type { Game } from '../sim/game';
import type { Room } from '../sim/room';
import { BirdcageRoom } from '../sim/rooms/birdcage';
import { DogsRoom } from '../sim/rooms/dogs';
import { FeliciaRoom } from '../sim/rooms/felicia';
import { FishbowlRoom } from '../sim/rooms/fishbowl';
import type { GameEvent } from '../sim/types';
import { SceneCamera } from './camera';
import { BUBBLE, DUST, GOLD, HEART, LEAF, MILK, Particles, SPARK, SPLASH, ZAP } from './effects';
import { shadowify } from './materials';
import { buildAlley } from './models';
import { RoomViews } from './rooms';
import { AlleyViews, CatView } from './views';

interface Transition {
  total: number;
  from: { x: number; y: number };
  to: { x: number; y: number };
  swapped: boolean;
}

type Mood = 'alley' | 'room' | 'felicia' | 'tank';

interface Lighting {
  background: number;
  fog: number;
  hemiSky: number;
  hemiGround: number;
  key: number;
  keyIntensity: number;
}

const MOODS: Record<Mood, Lighting> = {
  alley: {
    background: 0x141a24,
    fog: 0x141a24,
    hemiSky: 0xdde6ff,
    hemiGround: 0x2a2420,
    key: 0xfff1dc,
    keyIntensity: 1.6,
  },
  room: {
    background: 0x1a1612,
    fog: 0x1a1612,
    hemiSky: 0xfff0dc,
    hemiGround: 0x4a3a30,
    key: 0xffe4b8,
    keyIntensity: 1.8,
  },
  felicia: {
    background: 0x2a1020,
    fog: 0x2a1020,
    hemiSky: 0xffe0f0,
    hemiGround: 0x5a2a40,
    key: 0xffd8e8,
    keyIntensity: 1.7,
  },
  tank: {
    background: 0x0a2a4a,
    fog: 0x0a2a4a,
    hemiSky: 0xa0d8ff,
    hemiGround: 0x103050,
    key: 0xb8e0ff,
    keyIntensity: 1.3,
  },
};

export class WorldRenderer {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene = new THREE.Scene();
  readonly camera: SceneCamera;
  readonly particles = new Particles();
  readonly cat: CatView;
  private readonly alleyGroup = new THREE.Group();
  private alleyViews: AlleyViews | null = null;
  private roomViews: RoomViews | null = null;
  private roomBound: Room | null = null;
  private tankBound = false;
  private readonly composer: EffectComposer;
  private readonly bloom: UnrealBloomPass;
  private readonly hemi: THREE.HemisphereLight;
  private readonly key: THREE.DirectionalLight;
  private readonly fog: THREE.Fog;
  private mood: Mood | null = null;
  private readonly fade: HTMLDivElement;
  private game: Game;
  private time = 0;
  private bubbleTimer = 0;
  private readonly container: HTMLElement;
  private wasOnGround = true;
  private wasCaught = false;
  private lastFreeze = 0;
  private trans: Transition | null = null;
  private lastCat = { x: WORLD_W / 2, y: 0 };
  private lowDetail = false;
  /** No camera shake (transitions are switched off in the game config). */
  reduceMotion = false;

  constructor(container: HTMLElement, game: Game) {
    this.container = container;
    this.game = game;
    this.renderer = new THREE.WebGLRenderer({ antialias: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(container.clientWidth, container.clientHeight);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    container.appendChild(this.renderer.domElement);

    this.fade = document.createElement('div');
    this.fade.className = 'fade';
    container.appendChild(this.fade);

    this.fog = new THREE.Fog(0x141a24, 400, 900);
    this.scene.fog = this.fog;

    this.hemi = new THREE.HemisphereLight(0xdde6ff, 0x2a2420, 0.9);
    this.key = new THREE.DirectionalLight(0xfff1dc, 1.6);
    this.key.position.set(WORLD_W * 0.25, WORLD_H * 1.3, 240);
    this.key.target.position.set(WORLD_W / 2, WORLD_H / 3, 0);
    this.key.castShadow = true;
    this.key.shadow.mapSize.set(2048, 2048);
    const sc = this.key.shadow.camera;
    sc.left = -240;
    sc.right = 240;
    sc.top = 200;
    sc.bottom = -200;
    sc.near = 10;
    sc.far = 700;
    this.key.shadow.bias = -0.0006;
    this.key.shadow.normalBias = 0.6;
    const fill = new THREE.DirectionalLight(0x9fb8ff, 0.45);
    fill.position.set(WORLD_W * 1.2, WORLD_H * 0.6, 120);
    this.scene.add(this.hemi, this.key, this.key.target, fill, this.particles.points, this.alleyGroup);

    this.camera = new SceneCamera(container.clientWidth / container.clientHeight);
    this.cat = new CatView(this.scene);
    shadowify(this.cat.root);

    const size = new THREE.Vector2(container.clientWidth, container.clientHeight);
    this.composer = new EffectComposer(this.renderer);
    this.composer.addPass(new RenderPass(this.scene, this.camera.camera));
    this.bloom = new UnrealBloomPass(size, 0.35, 0.4, 0.92);
    this.composer.addPass(this.bloom);
    this.composer.addPass(new OutputPass());

    this.bind(game);

    new ResizeObserver(() => this.resize()).observe(container);
  }

  private resize(): void {
    const w = this.container.clientWidth;
    const h = this.container.clientHeight;
    this.renderer.setSize(w, h);
    this.composer.setSize(w, h);
    this.camera.resize(w / h);
  }

  /** Shadows, bloom and device pixels off for weaker machines. Takes effect at once. */
  setLowDetail(on: boolean): void {
    if (on === this.lowDetail) return;
    this.lowDetail = on;
    this.renderer.setPixelRatio(on ? 1 : Math.min(window.devicePixelRatio, 2));
    this.renderer.shadowMap.enabled = !on;
    this.key.castShadow = !on;
    this.bloom.enabled = !on;
    this.scene.traverse((o) => {
      const m = (o as THREE.Mesh).material as THREE.Material | THREE.Material[] | undefined;
      if (!m) return;
      for (const mat of Array.isArray(m) ? m : [m]) mat.needsUpdate = true;
    });
    this.resize();
  }

  /** Attach a (new) game: rebuild the scenery and snap the cat. */
  bind(game: Game): void {
    this.game = game;
    this.alleyGroup.clear();
    this.alleyViews?.dispose();
    this.roomViews?.dispose();
    this.roomViews = null;
    this.roomBound = null;
    this.trans = null;
    this.lastFreeze = 0;
    this.fade.style.opacity = '0';
    this.alleyGroup.add(buildAlley(game.alley));
    this.alleyViews = new AlleyViews(this.alleyGroup, game.alley);
    shadowify(this.alleyGroup);
    this.syncScene();
    this.cat.snap();
    this.lastCat = { x: game.cat.x + game.cat.w / 2, y: game.cat.y + game.cat.h / 2 };
  }

  /** Call after each simulation tick. */
  commit(): void {
    const g = this.game;
    const c = g.cat;
    if (g.freeze > this.lastFreeze) {
      // A hold began this tick: Tabs has already been moved to where he
      // is going. Remember where he was for the dolly in, and go from there.
      this.trans = {
        total: g.freeze,
        from: { ...this.lastCat },
        to: { x: c.x + c.w / 2, y: c.y + c.h / 2 },
        swapped: false,
      };
    }
    this.lastFreeze = g.freeze;

    if (this.trans && !this.trans.swapped) {
      if (this.progress(0) >= 0.5) {
        this.trans.swapped = true;
        this.syncScene();
        this.cat.snap();
      }
      // Otherwise keep showing the scene he left, untouched.
    } else {
      this.syncScene();
    }
    this.lastCat = { x: c.x + c.w / 2, y: c.y + c.h / 2 };
  }

  /** Make the scenery and views match the game's current scene, and commit them. */
  private syncScene(): void {
    const g = this.game;
    const c = g.cat;
    if (g.room !== this.roomBound) {
      this.roomViews?.dispose();
      this.roomViews = g.room ? new RoomViews(this.scene, g.room) : null;
      if (this.roomViews) shadowify(this.roomViews.group);
      this.roomBound = g.room;
      this.alleyGroup.visible = !g.room;
    }
    const tank = g.room instanceof FishbowlRoom && g.room.phase === 'tank';
    if (tank !== this.tankBound) {
      this.tankBound = tank;
    }
    this.setMood(!g.room ? 'alley' : tank ? 'tank' : g.room instanceof FeliciaRoom ? 'felicia' : 'room');

    this.cat.commit(c);
    if (g.room) {
      this.roomViews?.commit(g.room);
      this.cat.swimming = this.roomViews?.swimming ?? false;
      this.cat.gasping = this.roomViews?.gasping ?? false;
    } else {
      this.alleyViews?.commit(g.alley);
      this.cat.swimming = false;
      this.cat.gasping = false;
    }
    if (c.onGround && !this.wasOnGround && !this.cat.swimming) {
      this.particles.emit(c.x + c.w / 2, c.y, 0, 6, 25, DUST, 0.35, 2.2, 0.4);
    }
    this.wasOnGround = c.onGround;
    const caught = c.state === 'caught';
    if (caught && (!this.wasCaught || g.count % 4 === 0)) {
      this.particles.emit(c.x + c.w / 2, c.y + 5, 0, 4, 30, DUST, 0.5, 3, 0.8);
    }
    this.wasCaught = caught;
  }

  private setMood(mood: Mood): void {
    if (mood === this.mood) return;
    this.mood = mood;
    const l = MOODS[mood];
    this.scene.background = new THREE.Color(l.background);
    this.fog.color.set(l.fog);
    this.hemi.color.set(l.hemiSky);
    this.hemi.groundColor.set(l.hemiGround);
    this.key.color.set(l.key);
    this.key.intensity = l.keyIntensity;
  }

  /** Where the current transition is, 0..1, given the fraction of a tick elapsed. */
  private progress(alpha: number): number {
    const t = this.trans;
    if (!t) return 1;
    return Math.min(1, (t.total - this.game.freeze + alpha) / t.total);
  }

  /** Turn a simulation event into particles and camera shake. */
  onEvent(ev: GameEvent): void {
    const g = this.game;
    const c = g.cat;
    const cx = c.x + c.w / 2;
    const cy = c.y + c.h / 2;
    const p = this.particles;
    switch (ev.kind) {
      case 'caught':
        this.kick(7);
        break;
      case 'knocked':
        p.emit(cx, cy, 0, 8, 35, DUST, 0.4, 2.5, 0.6);
        if (ev.by === 'broom' || ev.by === 'spider') this.kick(3);
        break;
      case 'kiss':
        p.emit(cx, cy + 4, 4, 40, 45, HEART, 1.8, 4.5, 0.7, -0.4);
        break;
      case 'cageBroke': {
        const cage = g.room instanceof BirdcageRoom ? g.room.cage : null;
        if (cage) p.emit(cage.x + cage.w / 2, cage.y + 6, 4, 30, 70, GOLD, 0.9, 3, 0.9);
        this.kick(4);
        break;
      }
      case 'nudge':
        p.emit(cx, cy, 2, 6, 30, DUST, 0.4, 2.5, 0.5);
        break;
      case 'fernDown':
        p.emit(cx + c.facing * 8, cy + 6, 2, 16, 50, LEAF, 0.9, 2.8, 0.8);
        break;
      case 'fishEaten':
        p.emit(cx, cy, 2, 10, 20, BUBBLE, 1.2, 2.5, 0.6, -0.6);
        break;
      case 'dive':
        p.emit(WORLD_W / 2, WATER_SURFACE, 4, 40, 80, SPLASH, 0.8, 3.5, 1, 1);
        break;
      case 'surface':
        p.emit(cx, WATER_SURFACE, 3, 14, 40, SPLASH, 0.5, 2.5, 0.8, 1);
        break;
      case 'shock':
        p.emit(cx, cy, 4, 24, 60, ZAP, 0.4, 3, 1, 0);
        this.kick(3);
        break;
      case 'drowned':
        p.emit(cx, cy + 4, 2, 20, 25, BUBBLE, 1.5, 3, 0.6, -0.6);
        this.kick(2);
        break;
      case 'sweep': {
        const b = g.room?.broom;
        if (b) p.emit(b.x + b.w / 2, b.y, 6, 10, 25, DUST, 0.5, 2.5, 0.8);
        break;
      }
      case 'roomDone':
      case 'birdCaught':
      case 'bowlEmpty':
      case 'gift':
      case 'mouseCaught':
        p.emit(cx, cy + 4, 4, 18, 45, SPARK, 0.7, 3, 0.8, 0.3);
        break;
      case 'heartMended':
        p.emit(cx, cy, 2, 4, 20, HEART, 0.6, 2.5, 0.6, -0.3);
        break;
    }
  }

  private kick(amp: number): void {
    if (!this.reduceMotion) this.camera.shake(amp);
  }

  /** Call once per animation frame with the fraction of the current tick elapsed. */
  render(alpha: number, dt: number): void {
    this.time += dt;
    const t = this.trans;
    if (t) {
      const p = this.progress(alpha);
      if (p < 0.5) {
        this.camera.dolly(t.from.x, t.from.y, p * 2);
        this.fade.style.opacity = String(Math.min(1, p * 2));
      } else {
        const q = (p - 0.5) * 2;
        this.camera.dolly(t.to.x, t.to.y, 1 - q);
        this.fade.style.opacity = String(Math.max(0, 1 - q));
      }
      if (p >= 1 && this.game.freeze === 0) {
        this.trans = null;
        this.camera.dolly(t.to.x, t.to.y, 0);
        this.fade.style.opacity = '0';
      }
    }
    this.camera.update(dt);

    const showCat = !(t && !t.swapped) && !this.roomViews?.hidesCat;
    this.cat.root.visible = showCat;
    this.cat.present(alpha, this.time);
    if (this.roomViews) this.roomViews.present(alpha, this.time);
    else this.alleyViews?.present(alpha, this.time, dt);

    if (this.game.room instanceof DogsRoom && this.game.room.carton.state === 'pour') {
      this.bubbleTimer -= dt;
      if (this.bubbleTimer <= 0) {
        this.bubbleTimer = 0.05;
        const c = this.game.room.carton;
        this.particles.emit(c.x + c.w + 4, c.y + 2, 2, 2, 12, MILK, 0.5, 2, 0.2, 1.2);
      }
    }
    if (this.cat.swimming && showCat) {
      this.bubbleTimer -= dt;
      if (this.bubbleTimer <= 0) {
        this.bubbleTimer = 0.3 + Math.random() * 0.3;
        const c = this.game.cat;
        this.particles.emit(c.x + c.w / 2 + c.facing * 6, c.y + c.h, 2, 1, 8, BUBBLE, 1.4, 2, 0.3, -0.5);
      }
    }
    this.particles.update(dt);
    this.composer.render();
  }

  /** Title screen: the alley idles behind the menu with a slow drift. */
  renderAttract(dt: number): void {
    this.time += dt;
    const s = this.time * 0.25;
    this.camera.dolly(WORLD_W / 2 + Math.sin(s) * 50, WORLD_H / 2 + Math.cos(s * 0.7) * 25, 0.12);
    this.camera.update(dt);
    this.cat.root.visible = true;
    this.cat.present(0, this.time);
    this.alleyViews?.present(0, this.time, dt);
    this.particles.update(dt);
    this.composer.render();
  }

  setVisible(on: boolean): void {
    this.renderer.domElement.hidden = !on;
    this.fade.hidden = !on;
  }
}
